import { put, take, fork, call, all, select } from "redux-saga/effects";
import { UserAction, UserScope } from "./types";
import {
  enableScopeLoading,
  disableScopeLoading,
} from "../scope-loading/actions";
import * as Api from "../../service/api";
import {
  mapApiAcePlan,
  mapApiFlatData,
  mapApiFppPlan,
  mapApiAce2Plan,
  mapApiChildFlatData,
} from "./helpers";
import { ScopeErrorAction } from "../scope-error/types";
import RouteNames from "../../routes/type";
import { navigateTo } from "../../helper";
import { clearUserSearchInfo, increaseSearchTry } from "./actions";
import { cleanUpClaimInfo } from "../claim/actions";
import queryString from "query-string";
import { Ace2Plan } from "../../types/plan";

function* fetchHelperData() {
  while (true) {
    yield take(UserAction.FETCH_CLAIM_OPTIONS);
    yield put(enableScopeLoading(UserScope));

    try {
      const [
        materialTypes,
        manufacturers,
        colors,
        actionsTaken,
        specificDamages,
        howDidOccur,
        damageTypes,
        specificLocations,
      ] = yield all([
        call(Api.claimOptions.materialTypes),
        call(Api.claimOptions.manufacturers),
        call(Api.claimOptions.colors),
        //damage
        call(Api.claimOptions.actionsTaken),
        call(Api.claimOptions.specificDamages),
        call(Api.claimOptions.howDidOccur),
        call(Api.claimOptions.damageTypes),
        call(Api.claimOptions.specificLocations),
      ]);
      yield put({
        type: UserAction.SET_CLAIM_OPTIONS,
        payload: {
          materialTypes: materialTypes?.map(mapApiFlatData),
          manufacturers: manufacturers?.map(mapApiFlatData),
          colors: colors?.map(mapApiFlatData),
          actionsTaken: actionsTaken?.map(mapApiFlatData),
          howDidOccur: howDidOccur?.map(mapApiFlatData),
          specificLocations: specificLocations?.map(mapApiFlatData),
          damageTypes: damageTypes?.map(mapApiFlatData),
          specificDamages: specificDamages?.map(mapApiChildFlatData),
        },
      });
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: UserScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(UserScope));
    }
  }
}

function* fetchPlanById() {
  while (true) {
    try {
      const { payload: { id = "", customerId = "", planType = "FPP" } = {} } =
        yield take(UserAction.GET_PLAN_BY_ID);
      yield put(enableScopeLoading(UserScope));
      let mappedData = null;
      if (planType === "ACE") {
        const acePlans = yield call(
          Api.warranty.aceGetplanById,
          id,
          customerId
        );
        mappedData = mapApiAcePlan(acePlans?.customers?.[0]);
      } else if (planType === "FPP") {
        const plans = yield call(Api.warranty.getPlanById, id);
        mappedData = mapApiFppPlan(plans?.customers?.[0]);
      } else {
        yield navigateTo(`/${RouteNames.NO_WARRANTY_FOUND}`);
      }
      if (!mappedData) {
        yield navigateTo(`/${RouteNames.NO_WARRANTY_FOUND}`);
      }
      yield put({
        type: UserAction.SET_PLANS,
        payload: [mappedData],
      });
      yield navigateTo(`${RouteNames.SELECT_PLAN}`);
    } catch (error) {
      console.log("error:", error);
      yield navigateTo(`/${RouteNames.NO_WARRANTY_FOUND}`);
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: UserScope,
      });
    } finally {
      yield put(disableScopeLoading(UserScope));
    }
  }
}

function* fetchAceHelperData() {
  while (true) {
    const action = yield take(UserAction.FETCH_ACE_CLAIM_OPTIONS);
    yield put(enableScopeLoading(UserScope));
    const modelNo = action.payload;
    try {
      const [
        materialTypes,
        manufacturers,
        colors,
        actionsTaken,
        howDidOccur,
        damageTypes,
        // specificLocations,
      ] = yield all([
        call(Api.aceClaimOptions.materialTypes),
        call(Api.aceClaimOptions.manufacturers),
        call(Api.aceClaimOptions.colors),
        //damages
        call(Api.aceClaimOptions.actionsTaken),
        call(Api.aceClaimOptions.howDidOccur, modelNo),
        call(Api.aceClaimOptions.damageTypes, modelNo),
        // call(Api.aceClaimOptions.specificLocations, modelNo), // manage direct in damage box for ACE
      ]);
      yield put({
        type: UserAction.SET_CLAIM_OPTIONS,
        payload: {
          materialTypes: materialTypes.map(mapApiFlatData),
          manufacturers: manufacturers?.map(mapApiFlatData),
          colors: colors?.map(mapApiFlatData),
          actionsTaken: actionsTaken?.map(mapApiFlatData),
          howDidOccur: howDidOccur?.map(mapApiFlatData),
          // specificLocations: specificLocations?.map(mapApiFlatData),
          damageTypes: damageTypes?.map(mapApiFlatData),
        },
      });
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: UserScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(UserScope));
    }
  }
}

function* getPlansByCustomerInfo() {
  while (true) {
    const request = yield take(UserAction.GET_PLANS_BY_CUSTOMER_INFO);
    const payload = request.payload;
    try {
      yield put(increaseSearchTry());
      yield put(enableScopeLoading(UserScope));
      const { redirectExternal, ...body } = payload;
      const response = yield call(Api.warranty.searchPlan, body);
      const allPlan = [
        ...(response.customers || []),
        ...(response.aceCustomers || []),
      ];
      if (allPlan.length > 0) {
        const mappedData = allPlan
          .map((plan) => {
            if (plan.legacyMigrated == "1") {
              return mapApiAce2Plan(plan);
            } else if (plan.aceMigrated == "1") {
              return mapApiAcePlan(plan);
            } else if (plan.aceMigrated == "0") {
              return mapApiFppPlan(plan);
            }
            return false;
          })
          .filter(Boolean);
        if (redirectExternal && mappedData.length == 1) {
          const selectedPlan = mappedData[0] as Ace2Plan;
          if (
            selectedPlan.programMarketingName &&
            selectedPlan.programMarketingName.toLowerCase().includes("nikon")
          ) {
            window.location.href = `https://nikon-claim-request.myprotectall.com/create-claim`;
          } else {
            // TODO: Remove this line after deployemnt of ace2 claim file process on production
            window.location.href = `https://ace2-claim-request.myprotectall.com/${selectedPlan.id}?file_auto_claim=true`;
          }
        } else {
          yield put({
            type: UserAction.SET_PLANS,
            payload: mappedData,
          });
          yield put(clearUserSearchInfo());
          yield put(cleanUpClaimInfo());
          const parsed = queryString.parse(window.location.search);
          yield navigateTo(
            `/${RouteNames.SELECT_PLAN}?${queryString.stringify(parsed)}`
          );
        }
      } else {
        yield navigateTo(`/${RouteNames.NO_WARRANTY_FOUND}`);
      }
    } catch (error) {
      yield navigateTo(`/${RouteNames.NO_WARRANTY_FOUND}`);
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: UserScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(UserScope));
    }
  }
}

function* fetchAce2HelperData() {
  while (true) {
    yield take(UserAction.FETCH_ACE2_CLAIM_OPTIONS);
    yield put(enableScopeLoading(UserScope));

    try {
      const [
        manufacturers,
        colors,
        actionsTaken,
        howDidOccur,
        specificLocations,
      ] = yield all([
        call(Api.ace2ClaimOptions.manufacturers),
        call(Api.ace2ClaimOptions.colors),
        //damage
        call(Api.ace2ClaimOptions.actionsTaken),
        call(Api.ace2ClaimOptions.howDidOccur),
        call(Api.ace2ClaimOptions.specificLocations),
      ]);

      const CRMOptions = yield call(Api.ace2ClaimOptions.crmOptions);
      const materialTypes = Array.from(
        new Set(CRMOptions.map((item: any) => item.material))
      )
        .map((item) => ({ name: item }))
        .sort((a, b) => (b.name < a.name ? 1 : b.name > a.name ? -1 : 0));

      yield put({
        type: UserAction.SET_CLAIM_OPTIONS,
        payload: {
          materialTypes: materialTypes?.map(mapApiFlatData),
          manufacturers: manufacturers?.map(mapApiFlatData),
          colors: colors?.map(mapApiFlatData),
          actionsTaken: actionsTaken?.map(mapApiFlatData),
          howDidOccur: howDidOccur?.map(mapApiFlatData),
          specificLocations: specificLocations?.map(mapApiFlatData),
          specificDamageTypesCombined: CRMOptions,
        },
      });
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: UserScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(UserScope));
    }
  }
}

export default function* userSaga() {
  yield fork(fetchHelperData);
  yield fork(fetchAceHelperData);
  yield fork(fetchAce2HelperData);
  yield fork(fetchPlanById);
  yield fork(getPlansByCustomerInfo);
}
